/**
 * Record a voice-over over the video, the CapCut way: 3-2-1, the video plays from the playhead (muted, so the mic
 * hears only you) while you talk, stop, listen back, keep it or take it again. Kept takes are uploaded and placed
 * on the Voice-over track at the point where recording started; the music dips under it.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Play, RotateCcw, Square, X } from 'lucide-react';
import { clock } from './timelineOps';

type Phase = 'ready' | 'count' | 'rec' | 'review' | 'saving';

export function VoiceoverRecorder({ startAt, onPlayFrom, onStopPlayback, onKeep, onClose }: {
  startAt: number;
  onPlayFrom: (t: number) => void;
  onStopPlayback: () => void;
  onKeep: (blob: Blob, seconds: number) => Promise<void>;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('ready');
  const [count, setCount] = useState(3);
  const [secs, setSecs] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [take, setTake] = useState<{ blob: Blob; url: string; seconds: number } | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const t0 = useRef(0);
  const raf = useRef(0);

  useEffect(() => () => { cancelAnimationFrame(raf.current); stream.current?.getTracks().forEach((t) => t.stop()); onStopPlayback(); }, [onStopPlayback]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [onClose]);

  async function start() {
    setError(null);
    try {
      stream.current = stream.current || await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch { setError('The microphone is blocked. Allow it in the browser (the icon in the address bar) and try again.'); return; }
    // Level meter: the person sees the mic hears them.
    const ctx = new AudioContext(); const an = ctx.createAnalyser(); an.fftSize = 512;
    ctx.createMediaStreamSource(stream.current).connect(an);
    const buf = new Uint8Array(an.fftSize);
    const tick = () => { an.getByteTimeDomainData(buf); let m = 0; for (const v of buf) m = Math.max(m, Math.abs(v - 128)); setLevel(m / 128); if (rec.current?.state === 'recording') setSecs((performance.now() - t0.current) / 1000); raf.current = requestAnimationFrame(tick); };
    tick();
    setPhase('count'); setCount(3);
    for (let n = 3; n > 0; n--) { setCount(n); await new Promise((r) => setTimeout(r, 700)); }
    chunks.current = [];
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t)) || '';
    const r = new MediaRecorder(stream.current, type ? { mimeType: type } : undefined);
    r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    r.onstop = () => {
      const blob = new Blob(chunks.current, { type: r.mimeType || 'audio/webm' });
      const seconds = (performance.now() - t0.current) / 1000;
      setTake({ blob, url: URL.createObjectURL(blob), seconds });
      setPhase('review');
    };
    rec.current = r; r.start(250); t0.current = performance.now(); setSecs(0); setPhase('rec');
    onPlayFrom(startAt);
  }
  function stop() { rec.current?.stop(); onStopPlayback(); }
  async function keep() {
    if (!take) return;
    setPhase('saving');
    try { await onKeep(take.blob, take.seconds); }
    catch (e) { setError((e as Error).message); setPhase('review'); }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Record a voice-over" className="fixed bottom-6 left-1/2 z-[1100] w-[min(520px,calc(100vw-32px))] -translate-x-1/2 rounded-2xl bg-[#1d1726] p-4 text-[#e9e3f3] shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
      <div className="flex items-center gap-2">
        <Mic className="h-4 w-4 text-[#ff9cff]" />
        <span className="text-[14px] font-semibold">Voice-over</span>
        <span className="text-[12px] text-[#9a90a8]">from {clock(startAt)} — the video plays while you talk</span>
        <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-md p-1 text-[#9a90a8] hover:bg-white/10"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-3 flex items-center gap-3">
        {phase === 'ready' && <button type="button" onClick={() => void start()} className="inline-flex items-center gap-2 rounded-full bg-[#ff4d6d] px-4 py-2 text-[13px] font-semibold text-white hover:brightness-110"><span className="h-2.5 w-2.5 rounded-full bg-white" />Start recording</button>}
        {phase === 'count' && <span className="grid h-10 w-10 place-items-center rounded-full bg-[#ff4d6d] text-[18px] font-bold text-white">{count}</span>}
        {phase === 'rec' && <button type="button" onClick={stop} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-[#1d1726]"><Square className="h-3.5 w-3.5 fill-current" />Stop</button>}
        {(phase === 'review' || phase === 'saving') && take && <>
          <button type="button" onClick={() => void new Audio(take.url).play()} className="inline-flex items-center gap-1.5 rounded-full border border-[#4a4157] px-3 py-1.5 text-[12.5px] font-semibold hover:bg-white/10"><Play className="h-3.5 w-3.5" />Listen</button>
          <button type="button" disabled={phase === 'saving'} onClick={() => { setTake(null); setPhase('ready'); }} className="inline-flex items-center gap-1.5 rounded-full border border-[#4a4157] px-3 py-1.5 text-[12.5px] font-semibold hover:bg-white/10 disabled:opacity-40"><RotateCcw className="h-3.5 w-3.5" />Again</button>
          <button type="button" disabled={phase === 'saving'} onClick={() => void keep()} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#a9f600] px-4 py-1.5 text-[12.5px] font-bold text-[#2e024c] disabled:opacity-60">{phase === 'saving' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}Use this take ({take.seconds.toFixed(1)} s)</button>
        </>}
        {(phase === 'rec' || phase === 'count') && (
          <div className="flex flex-1 items-center gap-2">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#2a2235]"><div className="h-full rounded-full bg-[#3fd1bf] transition-[width] duration-75" style={{ width: `${Math.min(100, level * 160)}%` }} /></div>
            {phase === 'rec' && <span className="flex items-center gap-1.5 font-mono text-[12.5px] tabular-nums"><span className="h-2 w-2 animate-pulse rounded-full bg-[#ff4d6d]" />{secs.toFixed(1)} s</span>}
          </div>
        )}
      </div>
      {error && <p className="mt-2 text-[12px] text-[#ff9a8f]">{error}</p>}
      {phase === 'ready' && !error && <p className="mt-2 text-[11.5px] text-[#9a90a8]">Headphones help: the mic then hears only you. The music dips while you talk.</p>}
    </div>
  );
}
